import axios from 'axios';
import { useCallback, useEffect, useState } from 'react';

import {
  fetchRecentErrors,
  fetchTopRoutes,
  fetchUsageHistogram,
} from '@/api/endpoints/stats';
import type {
  RecentErrorsResponse,
  TopRoutesResponse,
  UsageHistogram,
} from '@/types/stats';
import { extractAxiosMessage } from '@/utils/axiosError';

export type StatsDataStatus = 'idle' | 'loading' | 'success' | 'error';

export interface UseStatsDataValue {
  readonly status: StatsDataStatus;
  readonly histogram: UsageHistogram | null;
  readonly topRoutes: TopRoutesResponse | null;
  readonly recentErrors: RecentErrorsResponse | null;
  readonly error: string | null;
  readonly refresh: () => void;
}

const HISTOGRAM_DAYS = 7;
const TOP_ROUTES_DAYS = 7;
const TOP_ROUTES_LIMIT = 10;
const RECENT_ERRORS_LIMIT = 20;

export function useStatsData(): UseStatsDataValue {
  const [status, setStatus] = useState<StatsDataStatus>('idle');
  const [histogram, setHistogram] = useState<UsageHistogram | null>(null);
  const [topRoutes, setTopRoutes] = useState<TopRoutesResponse | null>(null);
  const [recentErrors, setRecentErrors] = useState<RecentErrorsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadCounter, setReloadCounter] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setError(null);
    Promise.all([
      fetchUsageHistogram(HISTOGRAM_DAYS),
      fetchTopRoutes(TOP_ROUTES_LIMIT, TOP_ROUTES_DAYS),
      fetchRecentErrors(RECENT_ERRORS_LIMIT),
    ])
      .then(([hist, top, errors]) => {
        if (cancelled) return;
        setHistogram(hist);
        setTopRoutes(top);
        setRecentErrors(errors);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (cancelled || axios.isCancel(err)) return;
        setError(extractAxiosMessage(err) ?? 'unknown error');
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [reloadCounter]);

  const refresh = useCallback(() => {
    setReloadCounter((value) => value + 1);
  }, []);

  return { status, histogram, topRoutes, recentErrors, error, refresh };
}
